import * as Icons from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { PromptDisplay } from '../components/PromptDisplay';
import type { WorkflowDetail } from '../types';

interface SectionProps {
  workflow: WorkflowDetail;
}

export const ActivitiesSection = ({ workflow }: SectionProps) => (
  <div className="animate-in fade-in-50 duration-200">
    <section>
      <h2 className="text-xl font-semibold mb-6">Workflow Activities</h2>
      {workflow.activities && workflow.activities.length > 0 ? (
        <div className="space-y-4">
          {workflow.activities.map((activity, index) => (
            <div
              key={activity.id}
              className="p-4 md:p-6 rounded-lg border bg-card hover:shadow-sm transition-all duration-200 hover:border-border"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="flex-shrink-0 w-7 h-7 md:w-8 md:h-8 bg-primary/30 dark:bg-primary/60 text-primary dark:text-white rounded-full flex items-center justify-center text-xs md:text-sm font-medium">
                  {index + 1}
                </div>
                <div className="flex-grow min-w-0">
                  <h3 className="font-semibold text-base md:text-lg mb-2">{activity.title}</h3>
                  <div className="text-muted-foreground prose prose-sm max-w-none dark:prose-invert">
                    <ReactMarkdown>{activity.description}</ReactMarkdown>
                  </div>

                  {/* Prompts Section */}
                  {activity.prompts && activity.prompts.length > 0 && (
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Icons.MessageSquare className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">
                          Prompts ({activity.prompts.length})
                        </span>
                      </div>

                      <Accordion type="multiple" className="space-y-2">
                        {activity.prompts.map((prompt, promptIndex) => (
                          <AccordionItem
                            key={`${activity.id}-prompt-${promptIndex}`}
                            value={`prompt-${promptIndex}`}
                            className="border rounded-lg px-4"
                          >
                            <AccordionTrigger className="py-3 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <Icons.Code className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm font-medium">{prompt.promptName}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                              <PromptDisplay prompt={prompt} />
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 md:p-8 rounded-lg border bg-muted/30 text-center">
          <Icons.Activity className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No Activities</h3>
          <p className="text-muted-foreground text-sm md:text-base">
            No activities have been defined for this workflow.
          </p>
        </div>
      )}
    </section>
  </div>
);
